import * as React from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useAuth } from "@/providers";
import {
  FormPageHeading,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { type AgentRecord } from "@/shared/domain/agents";
import { useAgent, useUpdateAgent } from "@/shared/domain/agents/hooks";
import { Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services";

import { AgentForm } from "../AgentForm/AgentForm";

import styles from "./EditAgent.module.css";

const TITLE = "Edit agent";

/**
 * Edit one stored agent on the same form it was created on.
 *
 * The form only mounts once the agent has landed: it opens on the stored
 * values, and there is nothing to show before they exist.
 */
export function EditAgent() {
  const { agentId } = useParams<{ agentId: string }>();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  const agentQuery = useAgent(agentId, { orgId });
  // The org switch offers switching into the org that owns what is on screen.
  useRegisterViewingContext("agent", { orgId: agentQuery.data?.org_id });

  // The spinner is gated on `data === undefined` rather than `isLoading`:
  // TanStack reports `isLoading: false` for a disabled query, and `useAgent`
  // is disabled until `orgId` resolves, so that window would otherwise fall
  // through to a form with nothing in it.
  return (
    <PageLayout
      title={`Roboto - ${TITLE}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Agents}>Agents</Link>
          {agentId !== undefined && (
            <Link to={Goto.createAgentRoute(agentId)}>
              {agentQuery.data?.name ?? "Agent"}
            </Link>
          )}
          <span>{TITLE}</span>
        </PageBreadcrumbs>
      }
    >
      <div className={styles.page} data-testid="edit-agent-page">
        <FormPageHeading
          title={TITLE}
          subtitle="Change what this agent works on, what it may use, and how it is named."
        />
        {agentQuery.error !== null && (
          <p
            className={styles.notice}
            data-testid="edit-agent-error"
            role="alert"
          >
            {agentQuery.error.message}
          </p>
        )}
        {agentQuery.error === null && agentQuery.data === undefined && (
          <Spinner />
        )}
        {agentQuery.data !== undefined && (
          <Editor agent={agentQuery.data} orgId={orgId} />
        )}
      </div>
    </PageLayout>
  );
}

interface EditorProps {
  agent: AgentRecord;
  /** Undefined only in the window before an org resolves, which the page gates on. */
  orgId?: string;
}

function Editor({ agent, orgId }: EditorProps) {
  const navigate = useNavigate();
  const { mutateAsync: updateAgent } = useUpdateAgent();
  const backToAgent = React.useCallback(() => {
    void navigate(Goto.createAgentRoute(agent.agent_id));
  }, [navigate, agent.agent_id]);

  return (
    <AgentForm
      mode="edit"
      initial={agent}
      onSubmit={async (request) => {
        if (orgId === undefined) {
          throw new Error(
            "No organization is selected, so this agent cannot be saved.",
          );
        }
        // An agent's name is editable here — unlike a trigger's, it is not
        // the identifier — so the whole request is what the update carries.
        await updateAgent({ orgId, agentId: agent.agent_id, request });
        backToAgent();
      }}
      onCancel={backToAgent}
    />
  );
}
